import './ImagePicker.scss'
import React, { useRef, useState, CSSProperties } from 'react'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { Icon } from '../../index'
import AddImage from 'src/assets/v2/add-image.svg'
import DeleteImage from 'src/assets/v2/icon-delete.svg'
import { useImageOptimizer } from './useImageOptimizer'

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const DEFAULT_MAX_SIZE_MB = 5

interface ImagePickerProps {
  value: string | null
  onChange: (file: File | null) => void
  disabled?: boolean
  deleteLoading?: boolean
  suboptimal?: boolean
  onReoptimize?: () => Promise<File>
  style?: CSSProperties
  maxSizeMB?: number
}

const ImagePicker: React.FC<ImagePickerProps> = ({
  value,
  onChange,
  disabled = false,
  deleteLoading = false,
  suboptimal = false,
  onReoptimize,
  style,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [optimizing, setOptimizing] = useState(false)
  const [reoptimizing, setReoptimizing] = useState(false)
  const { optimize } = useImageOptimizer()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setError('Solo se permiten imágenes (jpg, png, webp)')
      return
    }

    if (file.size > maxSizeMB * 1_000_000) {
      setError(`La imagen no debe superar ${maxSizeMB}MB`)
      return
    }

    setError(null)
    setOptimizing(true)
    try {
      const optimized = await optimize(file)
      onChange(optimized)
    } catch {
      setError('No fue posible procesar la imagen')
    } finally {
      setOptimizing(false)
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setError(null)
    onChange(null)
  }

  const handleReoptimize = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onReoptimize) return
    setError(null)
    setReoptimizing(true)
    try {
      const file = await onReoptimize()
      const optimized = await optimize(file)
      onChange(optimized)
    } catch {
      setError('No fue posible optimizar la imagen')
    } finally {
      setReoptimizing(false)
    }
  }

  return (
    <div className="image-picker" style={style}>
      {value ? (
        <div className="image-picker__area" data-testid="image-picker-preview">
          <img src={value} alt="preview" className="image-picker__img" />
          {suboptimal && !reoptimizing && !disabled && (
            <button
              type="button"
              className="image-picker__optimize"
              onClick={handleReoptimize}>
              ⚠ Optimizar imagen
            </button>
          )}
          {reoptimizing && (
            <div className="image-picker__optimizing">
              <Icon icon={faSpinner} spin size={'1x'} /> Optimizando...
            </div>
          )}
          {!disabled && (
            <div
              className="image-picker__delete"
              onClick={handleDelete}
              data-testid="image-picker-delete">
              {deleteLoading ? (
                <Icon icon={faSpinner} spin size={'1x'} />
              ) : (
                <img src={DeleteImage} alt="eliminar" />
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          className={`image-picker__area image-picker__area--empty${
            disabled ? ' image-picker__area--empty--disabled' : ''
          }`}
          onClick={() => !disabled && !optimizing && inputRef.current?.click()}
          data-testid="image-picker-empty">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_MIME_TYPES.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={disabled || optimizing}
            data-testid="image-picker-input"
          />
          {optimizing ? (
            <Icon icon={faSpinner} spin size={'2x'} />
          ) : (
            <>
              <img src={AddImage} alt="agregar imagen" height={75} width={75} />
              <div className="image-picker__label">Agregar imagen</div>
            </>
          )}
        </div>
      )}
      {error && (
        <p className="image-picker__error" data-testid="image-picker-error">
          {error}
        </p>
      )}
    </div>
  )
}

export default ImagePicker
